import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang, useT } from "../context/LanguageContext";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

// Sunday-first day initials per locale (calendar grid layout starts on Sunday)
const DAY_LETTERS_BY_LANG = {
  en: ["S", "M", "T", "W", "T", "F", "S"],
  nl: ["Z", "M", "D", "W", "D", "V", "Z"],
};

function monthMatrix(year, month) {
  // first day of month
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7) cells.push(null);
  return cells;
}

function isoDate(d) { return d.toISOString().slice(0, 10); }
function fmtMonth(d, lang) {
  return d.toLocaleString(lang === "nl" ? "nl-NL" : "en-US", { month: "long", year: "numeric" });
}

export default function CalendarPage() {
  const { user } = useAuth();
  const t = useT();
  const { lang } = useLang();
  const DAY_LETTERS = DAY_LETTERS_BY_LANG[lang] || DAY_LETTERS_BY_LANG.en;
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayLogs, setDayLogs] = useState([]);
  const [streaks, setStreaks] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [sessionType, setSessionType] = useState("full");
  const [celebrateOpen, setCelebrateOpen] = useState(false);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const matrix = useMemo(() => monthMatrix(year, month), [year, month]);

  useEffect(() => {
    const start = isoDate(new Date(year, month, 1));
    const endD = new Date(year, month + 1, 0);
    const end = isoDate(endD);
    apiClient.get(`/lessons?start=${start}&end=${end}`).then((r) => setEvents(r.data.events));
  }, [year, month]);

  const eventsByDay = useMemo(() => {
    const g = {};
    for (const e of events) { (g[e.date] ||= []).push(e); }
    return g;
  }, [events]);

  const openDay = async (d) => {
    setSelectedDay(d);
    if (user?.role === "student") {
      try {
        const [logsR, sR] = await Promise.all([
          apiClient.get(`/practice/logs/${user.student_id}`),
          apiClient.get(`/practice/streaks/${user.student_id}`),
        ]);
        setDayLogs(logsR.data.logs.filter((l) => l.log_date === isoDate(d)));
        setStreaks(sR.data);
      } catch (e) { console.warn("Practice logs fetch failed", e); }
    }
  };

  const closeDay = () => setSelectedDay(null);

  const logPractice = async () => {
    if (!selectedDay || user?.role !== "student") return;
    try {
      const r = await apiClient.post("/practice/log", {
        student_id: user.student_id,
        log_date: isoDate(selectedDay),
        session_type: sessionType,
      });
      setLogOpen(false);
      setCelebrateOpen(true);
      setStreaks(r.data.streaks);
      const logsR = await apiClient.get(`/practice/logs/${user.student_id}`);
      setDayLogs(logsR.data.logs.filter((l) => l.log_date === isoDate(selectedDay)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Log failed"));
    }
  };

  const finishCelebration = () => { setCelebrateOpen(false); setSelectedDay(null); };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-bold">{t("Calendar")}</h1>
          <p style={{ color: "var(--app-text-muted)" }}>
            {user?.role === "student" ? t("Tap a day to log practice.") : t("Your lessons, auto-populated from each student's schedule.")}
          </p>
        </div>
        <div className="flex items-center gap-2 bento-card p-2">
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft /></Button>
          <div className="font-display font-bold text-lg min-w-[10rem] text-center" data-testid="cal-month-label">{fmtMonth(cursor, lang)}</div>
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today">{t("Today")}</Button>
        </div>
      </div>

      <div className="bento-card p-4">
        <div className="grid grid-cols-7 text-center text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--app-text-muted)" }}>
          {DAY_LETTERS.map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {matrix.map((d, idx) => {
            if (!d) return <div key={idx} className="aspect-square" />;
            const iso = isoDate(d);
            const evs = eventsByDay[iso] || [];
            const isToday = iso === isoDate(new Date());
            return (
              <button
                key={iso}
                onClick={() => openDay(d)}
                data-testid={`cal-day-${iso}`}
                className="aspect-square rounded-xl text-left p-1.5 sm:p-2 transition-all hover:-translate-y-0.5 relative overflow-hidden"
                style={{
                  background: isToday ? "var(--app-surface-2)" : "var(--app-surface)",
                  border: `2px solid ${isToday ? "var(--app-primary)" : "var(--app-border)"}`,
                }}
              >
                <div className="text-xs font-bold" style={{ color: isToday ? "var(--app-primary)" : "var(--app-text)" }}>{d.getDate()}</div>
                <div className="mt-1 space-y-0.5">
                  {evs.slice(0, 2).map((e) => (
                    <div key={e.id} className="truncate text-[10px] sm:text-xs px-1 py-0.5 rounded font-semibold"
                         style={{ background: e.type === "recital" ? "#0a0a0a" : "var(--app-primary)", color: "#fff" }}>
                      {e.type === "recital" ? "🎤" : ""}{e.type === "recital" ? e.title : e.student_name}
                    </div>
                  ))}
                  {evs.length > 2 && <div className="text-[10px]" style={{ color: "var(--app-text-muted)" }}>+{evs.length - 2} {t("more")}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day view dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && closeDay()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {selectedDay?.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </DialogTitle>
          </DialogHeader>

          {user?.role === "student" && streaks && (
            <div className="rounded-2xl p-4 my-2" style={{ background: "var(--app-surface-2)" }} data-testid="punch-card">
              <div className="text-xs uppercase tracking-wider font-bold">{t("Practice punch card")}</div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="font-display text-4xl font-bold">{streaks.daily_streak}</div>
                <div className="text-sm">{t("day streak")} {streaks.daily_streak >= 3 && "🔥"}</div>
              </div>
              <div className="mt-2 text-sm">
                {dayLogs.length > 0 ? <span className="font-semibold" style={{ color: "var(--app-primary)" }}>{t("✓ Logged today — keep it up!")}</span> : <span>{t("No practice logged yet for this day.")}</span>}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-sm font-bold uppercase tracking-wide">{t("Events")}</div>
            {(eventsByDay[selectedDay ? isoDate(selectedDay) : ""] || []).length === 0 && (
              <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>{t("Nothing scheduled.")}</div>
            )}
            {(eventsByDay[selectedDay ? isoDate(selectedDay) : ""] || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between p-3 rounded-xl border"
                   style={{ borderColor: "var(--app-border)" }}>
                <div>
                  <div className="font-semibold">{e.type === "recital" ? `🎤 ${e.title}` : `🎹 ${e.student_name}`}</div>
                  <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>{e.time} · {e.duration_minutes} {t("min")}{e.location ? ` · ${e.location}` : ""}</div>
                </div>
              </div>
            ))}
          </div>

          {user?.role === "student" && (
            <Button onClick={() => setLogOpen(true)} className="w-full h-12 btn-pill btn-accent justify-center mt-3" data-testid="open-log-practice">
              <ThumbsUp className="w-5 h-5 mr-2" /> {t("Log practice")}
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Log practice */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("Log your practice")}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-3">
            <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl" style={{ background: "#FFB703" }}>
              <ThumbsUp className="w-12 h-12" />
            </div>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger className="w-full" data-testid="session-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">{t("I did a full session")}</SelectItem>
                <SelectItem value="half">{t("I did a half session")}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={logPractice} className="w-full h-11 btn-pill btn-primary justify-center" data-testid="log-session-btn">{t("Log this session")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Celebration */}
      <Dialog open={celebrateOpen} onOpenChange={(o) => !o && finishCelebration()}>
        <DialogContent className="max-w-sm text-center">
          <div className="py-6">
            <div className="text-7xl animate-pop" aria-hidden>🎉</div>
            <div className="font-display text-3xl font-bold mt-3">{t("Awesome job, keep going!")}</div>
            <Button onClick={finishCelebration} className="mt-4 btn-pill btn-primary" data-testid="celebrate-ok">{t("Back to calendar")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
